import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Users, UserCircle2, TrendingUp, Megaphone, ClipboardList, Sparkles, CheckCircle2, Zap, Bell, Plus, LayoutGrid } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";
import { PRODUCTS, AI_SPECIALISTS, DASHBOARD_STATS } from "@/data/content";
import { ProductIcon } from "@/lib/icons";
import { Section, PrimaryLink, GhostLink, CtaRow } from "@/components/shared";

const PILLAR_ICONS = [Users, UserCircle2, TrendingUp, Megaphone, ClipboardList, Sparkles];

const Hero = () => {
  const { t } = useLanguage();
  return (
    <section className="relative overflow-hidden grid-bg noise">
      <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[900px] h-[600px] glow-orb pointer-events-none" />
      <div className="max-w-7xl mx-auto px-5 sm:px-8 pt-20 pb-16 sm:pt-28 sm:pb-24 relative">
        <div className="grid lg:grid-cols-2 gap-14 items-center">
          <div className="animate-fade-up">
            <p className="text-xs uppercase tracking-[0.25em] font-mono2 font-semibold text-cyan-400 mb-5">{t("home.eyebrow")}</p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.08] text-white mb-6">
              {t("home.h1a")} <span className="text-gradient">{t("home.h1b")}</span>
            </h1>
            <p className="text-base md:text-lg font-semibold text-indigo-300 mb-4">{t("home.sub1")}</p>
            <p className="text-base text-slate-300 leading-relaxed mb-3">{t("home.sub2")}</p>
            <p className="text-sm text-slate-400 leading-relaxed mb-8">{t("home.sub3")}</p>
            <div className="flex flex-wrap gap-3 mb-10">
              <PrimaryLink to="/signup" testId="hero-start-free">{t("common.startFree")}</PrimaryLink>
              <GhostLink to="/contact" testId="hero-book-demo">{t("common.bookDemo")}</GhostLink>
              <GhostLink to="/products" testId="hero-explore-products">{t("home.exploreProducts")}</GhostLink>
            </div>
            <div data-testid="trust-bar" className="flex flex-wrap gap-x-4 gap-y-2">
              {t("home.trust").map((item) => (
                <span key={item} className="flex items-center gap-1.5 text-xs text-slate-400">
                  <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" /> {item}
                </span>
              ))}
            </div>
          </div>

          <div className="relative animate-fade-up stagger-2 hidden lg:block">
            <div className="glass rounded-3xl p-3 shadow-2xl shadow-black/60 animate-float">
              <img
                src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
                alt="NEXUS dashboard preview"
                data-testid="hero-dashboard-img"
                className="rounded-2xl w-full object-cover aspect-[4/3]"
                loading="eager"
              />
            </div>
            <div className="absolute -left-6 top-10 glass rounded-2xl px-4 py-3 shadow-xl animate-float" style={{ animationDelay: "0.6s" }}>
              <p className="text-[10px] font-mono2 uppercase tracking-widest text-slate-500">ROAS</p>
              <p className="text-xl font-extrabold font-heading text-emerald-400">6.4X</p>
            </div>
            <div className="absolute -right-4 bottom-12 glass rounded-2xl px-4 py-3 shadow-xl animate-float" style={{ animationDelay: "1.2s" }}>
              <p className="text-[10px] font-mono2 uppercase tracking-widest text-slate-500">AI</p>
              <p className="text-xl font-extrabold font-heading text-cyan-400">3,824</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const Pillars = () => {
  const { t } = useLanguage();
  return (
    <Section eyebrow={t("home.pillarsEyebrow")} title={t("home.pillarsTitle")}>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {t("home.pillars").map((p, i) => {
          const Icon = PILLAR_ICONS[i];
          return (
            <div key={p.k} data-testid={`pillar-${p.k.toLowerCase()}`} className={`card-surface card-surface-hover rounded-2xl p-5 text-center animate-fade-up stagger-${i + 1}`}>
              <span className="inline-flex w-11 h-11 rounded-xl bg-indigo-500/15 items-center justify-center mb-3">
                <Icon className="w-5 h-5 text-indigo-400" />
              </span>
              <p className="text-sm font-bold font-heading text-white tracking-wide mb-1.5">{p.k}</p>
              <p className="text-xs text-slate-400 leading-relaxed">{p.d}</p>
            </div>
          );
        })}
      </div>
    </Section>
  );
};

export const ProductCards = ({ detailed = false }) => {
  const { t, pick } = useLanguage();
  return (
    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
      {PRODUCTS.map((p, i) => (
        <Link
          key={p.slug}
          to={`/products/${p.slug}`}
          data-testid={`product-card-${p.slug}`}
          className={`card-surface card-surface-hover rounded-3xl p-7 border border-slate-800/80 ${p.accent.border} group flex flex-col animate-fade-up ${i === 0 ? "md:col-span-2 lg:col-span-1" : ""}`}
        >
          <div className="flex items-start justify-between mb-5">
            <span className={`w-12 h-12 rounded-2xl bg-gradient-to-br ${p.accent.grad} flex items-center justify-center shadow-lg`}>
              <ProductIcon name={p.icon} className="w-6 h-6 text-white" />
            </span>
            <span className={`text-[10px] font-mono2 uppercase tracking-widest px-2.5 py-1 rounded-full ${p.accent.badge}`}>{p.short}</span>
          </div>
          <h3 className="text-xl font-semibold font-heading text-white mb-1">{p.name}</h3>
          <p className={`text-xs font-mono2 uppercase tracking-widest ${p.accent.text} mb-3`}>{pick(p.tagline)}</p>
          <p className="text-sm text-slate-400 leading-relaxed mb-5 flex-1">{pick(p.desc)}</p>
          {detailed && (
            <div className="flex flex-wrap gap-1.5 mb-5">
              {pick(p.features).slice(0, 5).map((f) => (
                <span key={f} className="text-[11px] text-slate-400 bg-slate-800/70 border border-slate-700/60 rounded-full px-2.5 py-1">{f}</span>
              ))}
              <span className="text-[11px] text-slate-500 px-1 py-1">+{pick(p.features).length - 5}</span>
            </div>
          )}
          <span className={`inline-flex items-center gap-2 text-sm font-semibold ${p.accent.text} group-hover:gap-3 transition-all`}>
            {t("common.exploreProduct")} {p.short} <ArrowRight className="w-4 h-4" />
          </span>
        </Link>
      ))}
      <Link
        to="/contact"
        data-testid="product-card-cta"
        className="rounded-3xl p-7 border border-dashed border-slate-700 hover:border-indigo-500/60 transition-colors flex flex-col items-center justify-center text-center gap-3 group"
      >
        <span className="w-12 h-12 rounded-2xl border border-slate-700 group-hover:border-indigo-500/60 flex items-center justify-center transition-colors">
          <Plus className="w-6 h-6 text-slate-500 group-hover:text-indigo-400 transition-colors" />
        </span>
        <p className="text-sm font-semibold text-slate-300">{t("common.talkToSales")}</p>
        <p className="text-xs text-slate-500">{t("home.ctaSub")}</p>
      </Link>
    </div>
  );
};

const STEP_PRODUCT = {
  "ADS BOOSTER": "ads-booster", ADS: "ads-booster", "WASAP AI": "wasap", HR: "hr",
  STORE: "store", "TRAVEL CRM": "travel", "BLDG OPS": "building-operations",
};

const FLOW_ACCENTS = [
  { bar: "from-rose-500 to-pink-400", text: "text-rose-400" },
  { bar: "from-emerald-500 to-teal-400", text: "text-emerald-400" },
  { bar: "from-orange-500 to-amber-400", text: "text-orange-400" },
  { bar: "from-sky-500 to-cyan-400", text: "text-sky-400" },
];

const Flows = () => {
  const { t } = useLanguage();
  return (
    <Section eyebrow={t("home.flowEyebrow")} title={t("home.flowTitle")} sub={t("home.flowSub")} className="bg-[#05070B]">
      <div className="grid md:grid-cols-2 gap-5">
        {t("home.flows").map((flow, fi) => {
          const accent = FLOW_ACCENTS[fi % FLOW_ACCENTS.length];
          const productsInFlow = [...new Set(flow.steps.map((s) => STEP_PRODUCT[s]).filter(Boolean))];
          return (
            <div key={flow.name} data-testid={`flow-${fi}`} className="card-surface card-surface-hover rounded-3xl p-6 sm:p-7 relative overflow-hidden group">
              <div className={`absolute top-0 left-0 right-0 h-[3px] bg-gradient-to-r ${accent.bar} opacity-70`} />
              <div className="flex items-center justify-between mb-5">
                <p className={`text-xs font-mono2 uppercase tracking-[0.2em] font-semibold ${accent.text}`}>{flow.name}</p>
                <div className="flex -space-x-2">
                  {productsInFlow.map((slug) => {
                    const p = PRODUCTS.find((x) => x.slug === slug);
                    return p ? (
                      <Link
                        key={slug}
                        to={`/products/${slug}`}
                        data-testid={`flow-${fi}-product-${slug}`}
                        title={p.name}
                        className={`w-8 h-8 rounded-full bg-gradient-to-br ${p.accent.grad} border-2 border-[#0B0F16] flex items-center justify-center hover:scale-110 hover:z-10 transition-transform`}
                      >
                        <ProductIcon name={p.icon} className="w-3.5 h-3.5 text-white" />
                      </Link>
                    ) : null;
                  })}
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-y-3">
                {flow.steps.map((step, i) => {
                  const slug = STEP_PRODUCT[step];
                  const p = slug ? PRODUCTS.find((x) => x.slug === slug) : null;
                  return (
                    <span key={step} className="flex items-center">
                      {p ? (
                        <Link
                          to={`/products/${p.slug}`}
                          className={`flex items-center gap-1.5 text-[11px] font-mono2 font-semibold rounded-lg px-2.5 py-1.5 border transition-colors ${p.accent.badge} border-transparent ${p.accent.border.replace("hover:", "")} hover:border-current`}
                        >
                          <ProductIcon name={p.icon} className="w-3 h-3" />
                          {step}
                        </Link>
                      ) : (
                        <span className="text-[11px] font-mono2 font-semibold text-slate-200 bg-slate-800/90 border border-slate-700 rounded-lg px-2.5 py-1.5">{step}</span>
                      )}
                      {i < flow.steps.length - 1 && <span className={`mx-1.5 ${accent.text} flow-pulse font-mono2`}>→</span>}
                    </span>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </Section>
  );
};

const AccountMock = () => {
  const { t, pick } = useLanguage();
  const activeProducts = ["hr", "wasap", "ads-booster", "store"];
  const activeCount = activeProducts.length;
  return (
    <Section eyebrow={t("home.accountEyebrow")} title={t("home.accountTitle")} sub={t("home.accountSub")}>
      <div className="max-w-3xl mx-auto glass rounded-3xl overflow-hidden shadow-2xl shadow-black/50">
        <div className="relative px-6 sm:px-8 pt-7 pb-6 border-b border-slate-800 bg-gradient-to-r from-indigo-500/10 via-transparent to-cyan-500/10">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <span className="w-12 h-12 rounded-2xl bg-gradient-to-br from-indigo-500 to-cyan-400 flex items-center justify-center font-heading font-extrabold text-white text-lg shadow-lg shadow-indigo-500/40">A</span>
              <div>
                <p className="font-heading font-bold text-white text-lg tracking-tight">ABC HOLDINGS</p>
                <p className="text-[11px] font-mono2 uppercase tracking-widest text-slate-500">{t("home.accountEyebrow")}</p>
              </div>
            </div>
            <span className="flex items-center gap-2 text-[10px] font-mono2 uppercase tracking-widest text-emerald-400 bg-emerald-500/10 border border-emerald-500/30 rounded-full px-3 py-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 flow-pulse" /> LIVE
            </span>
          </div>
          <div className="mt-5">
            <div className="flex justify-between text-[10px] font-mono2 uppercase tracking-widest text-slate-500 mb-2">
              <span>MY PRODUCTS</span>
              <span>{activeCount} / {PRODUCTS.length} {t("common.active")}</span>
            </div>
            <div className="h-1.5 rounded-full bg-slate-800 overflow-hidden">
              <div className="h-full rounded-full bg-gradient-to-r from-indigo-500 to-cyan-400 transition-all duration-700" style={{ width: `${(activeCount / PRODUCTS.length) * 100}%` }} />
            </div>
          </div>
        </div>

        <div className="p-4 sm:p-5 space-y-2">
          {PRODUCTS.map((p) => {
            const active = activeProducts.includes(p.slug);
            return (
              <div
                key={p.slug}
                data-testid={`account-product-${p.slug}`}
                className={`flex items-center justify-between rounded-xl border px-4 py-3 transition-colors ${
                  active ? "bg-slate-800/60 border-slate-700/80 hover:border-slate-600" : "bg-transparent border-slate-800/60 opacity-60 hover:opacity-90"
                }`}
              >
                <span className="flex items-center gap-3 min-w-0">
                  <span className={`w-8 h-8 rounded-lg bg-gradient-to-br ${p.accent.grad} flex items-center justify-center shrink-0 ${active ? "" : "grayscale opacity-60"}`}>
                    <ProductIcon name={p.icon} className="w-4 h-4 text-white" />
                  </span>
                  <span className="min-w-0">
                    <span className="block text-sm font-semibold text-slate-100 truncate">{p.name}</span>
                    <span className="block text-[10px] font-mono2 uppercase tracking-widest text-slate-500 truncate">{pick(p.tagline)}</span>
                  </span>
                </span>
                <span className={`flex items-center gap-1.5 text-[10px] font-mono2 uppercase tracking-widest px-2.5 py-1 rounded-full shrink-0 ml-3 ${
                  active ? "bg-emerald-500/15 text-emerald-400" : "bg-slate-700/40 text-slate-500"
                }`}>
                  <span className={`w-1.5 h-1.5 rounded-full ${active ? "bg-emerald-400" : "bg-slate-500"}`} />
                  {active ? t("common.active") : t("common.notSubscribed")}
                </span>
              </div>
            );
          })}
          <Link to="/pricing" data-testid="account-add-product" className="flex items-center justify-center gap-2 rounded-xl border border-dashed border-indigo-500/40 hover:border-indigo-400 hover:bg-indigo-500/10 px-4 py-3.5 text-sm font-semibold text-indigo-300 transition-colors">
            <Plus className="w-4 h-4" /> {t("common.addProduct")}
          </Link>
        </div>

        <p className="text-center text-xs text-slate-500 pb-6">{t("home.switcherTitle")}</p>
      </div>
    </Section>
  );
};

const INSIGHT_TABS = {
  en: [
    { k: "SALES", lines: ["Sales up 12% vs last month", "Top product: Umrah VIP Package", "42 hot leads awaiting contact"] },
    { k: "HR", lines: ["96% attendance today", "7 approvals overdue", "Payroll runs in 3 days"] },
    { k: "MARKETING", lines: ["Campaign A ROAS 8.2X", "Creative fatigue detected on Ad Set 3", "Cost per lead down 18%"] },
    { k: "CUSTOMERS", lines: ["NPS 62 this quarter", "3,824 AI conversations resolved", "84% first-response under 60s"] },
    { k: "OPERATIONS", lines: ["2 branches below target", "Stock reorder: Product X", "All systems operational"] },
    { k: "FINANCE", lines: ["Cash position RM412K", "Outstanding invoices RM86K", "Expense claims pending: 14"] },
    { k: "AI INSIGHTS", lines: ["AI saved ~31 staff hours this week", "Lead qualification accuracy 91%", "Suggested: retarget Cart Abandoners"] },
  ],
  bm: [
    { k: "JUALAN", lines: ["Jualan naik 12% berbanding bulan lepas", "Produk teratas: Pakej Umrah VIP", "42 lead panas menanti hubungan"] },
    { k: "HR", lines: ["Kehadiran 96% hari ini", "7 kelulusan tertangguh", "Payroll diproses dalam 3 hari"] },
    { k: "PEMASARAN", lines: ["Kempen A ROAS 8.2X", "Kelesuan kreatif dikesan pada Ad Set 3", "Kos per lead turun 18%"] },
    { k: "PELANGGAN", lines: ["NPS 62 suku ini", "3,824 perbualan AI diselesaikan", "84% respons pertama bawah 60s"] },
    { k: "OPERASI", lines: ["2 cawangan bawah sasaran", "Pesanan semula stok: Produk X", "Semua sistem beroperasi"] },
    { k: "KEWANGAN", lines: ["Kedudukan tunai RM412K", "Invois tertunggak RM86K", "Tuntutan perbelanjaan: 14"] },
    { k: "WAWASAN AI", lines: ["AI menjimatkan ~31 jam minggu ini", "Ketepatan kelayakan lead 91%", "Cadangan: sasaran semula Cart Abandoners"] },
  ],
};

const DashboardMock = () => {
  const { t, lang } = useLanguage();
  const tabs = INSIGHT_TABS[lang] || INSIGHT_TABS.en;
  const [tab, setTab] = useState(0);
  return (
    <Section eyebrow={t("home.dashEyebrow")} title={t("home.dashTitle")} className="bg-[#05070B]">
      <div className="glass rounded-3xl p-6 sm:p-10">
        <p className="text-xs font-mono2 uppercase tracking-[0.3em] text-indigo-400 mb-6">{t("home.dashGreeting")}, ABC HOLDINGS</p>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-9 gap-3 mb-10">
          {DASHBOARD_STATS.map((s, i) => (
            <div key={i} data-testid={`dash-stat-${i}`} className="card-surface rounded-2xl p-4 text-center">
              <p className="text-lg sm:text-xl font-extrabold font-heading text-white">{s.value}</p>
              <p className="text-[10px] uppercase tracking-wider text-slate-500 mt-1">{s.label[lang] || s.label.en}</p>
            </div>
          ))}
        </div>
        <p className="text-xs font-mono2 uppercase tracking-[0.3em] text-cyan-400 mb-4">{t("home.dashInsights")}</p>
        <div className="flex flex-wrap gap-2 mb-6">
          {tabs.map((tb, i) => (
            <button
              key={tb.k}
              data-testid={`insight-tab-${i}`}
              onClick={() => setTab(i)}
              className={`text-xs font-semibold rounded-full px-4 py-2 transition-colors ${tab === i ? "bg-indigo-500 text-white" : "bg-slate-800/70 text-slate-400 hover:text-white"}`}
            >
              {tb.k}
            </button>
          ))}
        </div>
        <div data-testid="insight-panel" className="card-surface rounded-2xl p-6 space-y-3 animate-fade-up" key={tab}>
          {tabs[tab].lines.map((l) => (
            <p key={l} className="flex items-center gap-3 text-sm text-slate-300">
              <Zap className="w-4 h-4 text-cyan-400 shrink-0" /> {l}
            </p>
          ))}
        </div>
      </div>
    </Section>
  );
};

const UniversalAI = () => {
  const { t } = useLanguage();
  return (
    <Section eyebrow={t("home.aiEyebrow")} title={t("home.aiTitle")} sub={t("home.aiSub")}>
      <div className="max-w-3xl mx-auto mb-14">
        <p className="text-xs font-mono2 uppercase tracking-[0.25em] text-slate-500 mb-4 text-center">{t("home.aiTry")}</p>
        <div className="flex flex-wrap justify-center gap-2.5">
          {t("home.aiQuestions").map((q) => (
            <span key={q} data-testid={`ai-question-${q.slice(0, 12).replace(/\s+/g, "-")}`} className="text-sm text-cyan-300 bg-cyan-500/10 border border-cyan-500/30 rounded-full px-4 py-2">
              “{q}”
            </span>
          ))}
        </div>
      </div>
      <p className="text-center text-xs font-mono2 uppercase tracking-[0.25em] text-slate-500 mb-6">{t("home.specialistsTitle")}</p>
      <div className="flex flex-wrap justify-center gap-2.5 max-w-3xl mx-auto">
        {AI_SPECIALISTS.map((s) => (
          <span key={s} className="flex items-center gap-2 text-xs font-medium text-slate-300 bg-slate-800/70 border border-slate-700/70 rounded-full px-4 py-2">
            <Sparkles className="w-3.5 h-3.5 text-indigo-400" /> {s}
          </span>
        ))}
      </div>
    </Section>
  );
};

const Automation = () => {
  const { t } = useLanguage();
  const ex1 = t("home.builderEx1");
  const ex2 = t("home.builderEx2");
  const BuilderCard = ({ ex, id }) => (
    <div data-testid={`automation-example-${id}`} className="card-surface rounded-2xl p-6">
      <div className="space-y-3">
        <div className="flex items-center gap-3">
          <span className="text-[10px] font-mono2 uppercase tracking-widest text-white bg-indigo-500 rounded-md px-2 py-1">{t("home.builderWhen")}</span>
          <span className="text-sm text-slate-200 font-medium">{ex.when}</span>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-[10px] font-mono2 uppercase tracking-widest text-white bg-amber-500 rounded-md px-2 py-1">{t("home.builderIf")}</span>
          <span className="text-sm text-slate-200 font-medium">{ex.cond}</span>
        </div>
        <div className="flex items-start gap-3">
          <span className="text-[10px] font-mono2 uppercase tracking-widest text-white bg-emerald-500 rounded-md px-2 py-1 mt-0.5">{t("home.builderThen")}</span>
          <span className="flex flex-wrap gap-2">
            {ex.then.map((a) => (
              <span key={a} className="text-xs text-slate-300 bg-slate-800 border border-slate-700 rounded-lg px-2.5 py-1.5">{a}</span>
            ))}
          </span>
        </div>
      </div>
    </div>
  );
  return (
    <Section eyebrow={t("home.autoEyebrow")} title={t("home.autoTitle")} sub={t("home.autoNote")} className="bg-[#05070B]">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-14">
        {t("home.autoItems").map((item, i) => (
          <div key={item} data-testid={`auto-item-${i}`} className="card-surface rounded-2xl p-4 text-xs text-slate-300 leading-relaxed flex items-start gap-2">
            <Sparkles className="w-3.5 h-3.5 text-cyan-400 shrink-0 mt-0.5" /> {item}
          </div>
        ))}
      </div>
      <p className="text-center text-xs font-mono2 uppercase tracking-[0.25em] text-indigo-400 mb-6">{t("home.builderTitle")}</p>
      <div className="grid md:grid-cols-2 gap-5 max-w-4xl mx-auto">
        <BuilderCard ex={ex1} id="1" />
        <BuilderCard ex={ex2} id="2" />
      </div>
    </Section>
  );
};

const CommandCentre = () => {
  const { t } = useLanguage();
  return (
    <Section eyebrow={t("home.ccEyebrow")} title={t("home.ccTitle")}>
      <div className="max-w-3xl mx-auto space-y-3">
        {t("home.ccActions").map((a, i) => (
          <div key={a} data-testid={`cc-action-${i}`} className="card-surface card-surface-hover rounded-2xl px-6 py-4 flex items-center gap-4">
            <span className="w-9 h-9 rounded-xl bg-amber-500/15 flex items-center justify-center shrink-0">
              <Bell className="w-4 h-4 text-amber-400" />
            </span>
            <p className="text-sm text-slate-200">{a}</p>
            <ArrowRight className="w-4 h-4 text-slate-600 ml-auto shrink-0" />
          </div>
        ))}
      </div>
      <div className="max-w-3xl mx-auto mt-10 card-surface rounded-3xl p-7 border-indigo-500/30">
        <p className="text-sm font-heading font-bold text-white mb-1">{t("home.crossTitle")}</p>
        <p className="text-xs text-slate-500 mb-4">{t("home.crossSub")}</p>
        <div className="flex flex-wrap gap-2.5">
          {t("home.crossAdd").map((a) => (
            <span key={a} className="text-xs text-indigo-300 bg-indigo-500/10 border border-indigo-500/30 rounded-full px-4 py-2">{a}</span>
          ))}
        </div>
      </div>
    </Section>
  );
};

const FinalCta = () => {
  const { t } = useLanguage();
  return (
    <section className="relative py-24 sm:py-32 px-5 sm:px-8 overflow-hidden">
      <div className="absolute inset-0 grid-bg opacity-60" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[400px] glow-orb pointer-events-none" />
      <div className="relative max-w-4xl mx-auto text-center">
        <p className="text-xs uppercase tracking-[0.25em] font-mono2 font-semibold text-cyan-400 mb-5">{t("home.ctaEyebrow")}</p>
        <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold font-heading tracking-tight text-white mb-6">{t("home.ctaTitle")}</h2>
        <p className="text-base text-slate-400 leading-relaxed mb-10 max-w-2xl mx-auto">{t("home.ctaSub")}</p>
        <CtaRow />
        <div className="mt-6">
          <GhostLink to="/contact" testId="cta-talk-sales">{t("common.talkToSales")}</GhostLink>
        </div>
        <p className="mt-12 text-[11px] font-mono2 uppercase tracking-[0.3em] text-indigo-400/70">{t("footer.promise")}</p>
      </div>
    </section>
  );
};

const Home = () => {
  const { t } = useLanguage();
  return (
    <div data-testid="home-page">
      <Hero />
      <Pillars />
      <Section eyebrow={t("home.ecoEyebrow")} title={t("home.ecoTitle")} sub={t("home.ecoSub")} className="pt-0">
        <ProductCards />
      </Section>
      <Flows />
      <AccountMock />
      <DashboardMock />
      <UniversalAI />
      <Automation />
      <CommandCentre />
      <FinalCta />
    </div>
  );
};

export default Home;
